import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { RefreshCw, Building2, Wallet } from 'lucide-react';
import { apiClient } from '../../utils/apiHelper';

const generateFYOptions = () => {
  const currentYear = new Date().getFullYear();
  const currentMonth = new Date().getMonth(); // 0-indexed
  const options = [];
  // Indian FY: April to March
  const startFY = currentMonth >= 3 ? currentYear : currentYear - 1;
  for (let i = 0; i < 6; i++) {
    const fy = startFY - i;
    options.push({
      label: `FY ${fy}-${(fy + 1).toString().slice(-2)}`,
      start: `${fy}-04-01`,
      end: `${fy + 1}-03-31`
    });
  }
  return options;
};

const BalanceSheet = () => {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [selectedFY, setSelectedFY] = useState('');
  const fyOptions = generateFYOptions();

  useEffect(() => { fetchReport(); }, []);

  const fetchReport = async () => {
    setLoading(true);
    try {
      const params = {};
      if (startDate) params.start_date = startDate;
      if (endDate) params.end_date = endDate;
      const response = await apiClient.get('/api/accounts/balance-sheet', { params });
      if (response.data) {
        setReport(response.data);
      }
    } catch (error) {
      console.error('Error fetching balance sheet:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFYChange = (value) => {
    setSelectedFY(value);
    if (value === '') {
      setStartDate('');
      setEndDate('');
    } else {
      const fy = fyOptions.find(f => f.label === value);
      if (fy) {
        setStartDate(fy.start);
        setEndDate(fy.end);
      }
    }
  };

  return (
    <div className="p-6">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between flex-wrap gap-4">
          <CardTitle className="flex items-center gap-2">
            <Building2 className="w-6 h-6" />
            Balance Sheet
          </CardTitle>
          <div className="flex gap-3 items-end flex-wrap" data-testid="balance-sheet-filters">
            <div>
              <Label>Financial Year</Label>
              <select
                value={selectedFY}
                onChange={(e) => handleFYChange(e.target.value)}
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                data-testid="balance-sheet-fy"
              >
                <option value="">All Time</option>
                {fyOptions.map(fy => (
                  <option key={fy.label} value={fy.label}>{fy.label}</option>
                ))}
              </select>
            </div>
            <div>
              <Label>From Date</Label>
              <Input type="date" value={startDate} onChange={(e) => { setStartDate(e.target.value); setSelectedFY(''); }} data-testid="balance-sheet-from-date" />
            </div>
            <div>
              <Label>To Date</Label>
              <Input type="date" value={endDate} onChange={(e) => { setEndDate(e.target.value); setSelectedFY(''); }} data-testid="balance-sheet-to-date" />
            </div>
            <Button onClick={fetchReport} disabled={loading} data-testid="balance-sheet-generate-btn">
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              {loading ? 'Loading...' : 'Generate'}
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {report && (
            <div className="grid grid-cols-2 gap-6">
              <div>
                <div className="bg-green-50 p-4 rounded-lg mb-4">
                  <h3 className="text-xl font-bold text-green-700 mb-4">ASSETS</h3>
                  
                  <div className="space-y-4">
                    <div>
                      <h4 className="font-semibold text-gray-700 mb-2">Fixed Assets</h4>
                      <div className="space-y-1 ml-4">
                        {report.assets?.fixed_assets?.length > 0 ? (
                          report.assets.fixed_assets.map((asset, idx) => (
                            <div key={idx} className="flex justify-between text-sm">
                              <span>{asset.asset_name}</span>
                              <span>₹{(asset.current_value || 0).toFixed(2)}</span>
                            </div>
                          ))
                        ) : (
                          <p className="text-sm text-gray-500">No fixed assets</p>
                        )}
                        <div className="flex justify-between font-semibold border-t pt-1">
                          <span>Total Fixed Assets</span>
                          <span>₹{(report.assets?.total_fixed_assets || 0).toFixed(2)}</span>
                        </div>
                      </div>
                    </div>

                    <div>
                      <h4 className="font-semibold text-gray-700 mb-2">Current Assets</h4>
                      <div className="space-y-1 ml-4">
                        <div className="flex justify-between text-sm">
                          <span>Cash Balance</span>
                          <span>₹{(report.assets?.cash_balance || 0).toFixed(2)}</span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span>Bank Balance</span>
                          <span>₹{(report.assets?.bank_balance || 0).toFixed(2)}</span>
                        </div>
                        <div className="flex justify-between font-semibold border-t pt-1">
                          <span>Total Current Assets</span>
                          <span>₹{(report.assets?.total_current_assets || 0).toFixed(2)}</span>
                        </div>
                      </div>
                    </div>

                    <div className="border-t-2 pt-2 mt-4">
                      <div className="flex justify-between font-bold text-lg">
                        <span>TOTAL ASSETS</span>
                        <span className="text-green-700">₹{(report.assets?.total_assets || 0).toFixed(2)}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div>
                <div className="bg-red-50 p-4 rounded-lg mb-4">
                  <h3 className="text-xl font-bold text-red-700 mb-4">LIABILITIES & CAPITAL</h3>
                  
                  <div className="space-y-4">
                    <div>
                      <h4 className="font-semibold text-gray-700 mb-2">Liabilities</h4>
                      <div className="space-y-1 ml-4">
                        {report.liabilities?.liabilities?.length > 0 ? (
                          report.liabilities.liabilities.map((liability, idx) => (
                            <div key={idx} className="flex justify-between text-sm">
                              <span>{liability.liability_name}</span>
                              <span>₹{(liability.amount || 0).toFixed(2)}</span>
                            </div>
                          ))
                        ) : (
                          <p className="text-sm text-gray-500">No liabilities</p>
                        )}
                        <div className="flex justify-between font-semibold border-t pt-1">
                          <span>Total Liabilities</span>
                          <span>₹{(report.liabilities?.total_liabilities || 0).toFixed(2)}</span>
                        </div>
                      </div>
                    </div>

                    <div>
                      <h4 className="font-semibold text-gray-700 mb-2">Capital</h4>
                      <div className="space-y-1 ml-4">
                        <div className="flex justify-between text-sm">
                          <span>Owner's Capital</span>
                          <span>₹{(report.liabilities?.capital || 0).toFixed(2)}</span>
                        </div>
                      </div>
                    </div>

                    <div className="border-t-2 pt-2 mt-4">
                      <div className="flex justify-between font-bold text-lg">
                        <span>TOTAL</span>
                        <span className="text-red-700">₹{(report.liabilities?.total || 0).toFixed(2)}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
          
          {!report && !loading && (
            <div className="text-center py-8 text-gray-500">
              <Wallet className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>Click "Generate" to load Balance Sheet</p>
            </div>
          )}

          <div className="mt-6 p-4 bg-blue-50 rounded-lg">
            <p className="text-sm text-gray-600">• Balance Sheet Equation: Assets = Liabilities + Capital</p>
            <p className="text-sm text-gray-600">• Capital = Assets - Liabilities</p>
            <p className="text-sm text-gray-600">• Indian Financial Year: April 1 to March 31</p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default BalanceSheet;
